import {HttpErrorResponse} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {ToastMessageUtils} from '@app/core/utils/toast-message.utils';
import {EToastMessageOptions, EToastMessageSeverity} from '@app/shared/enums/toast-message.enum';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {MessageService} from 'primeng/api';
import {catchError, map, of, switchMap} from 'rxjs';
import {IStaticPages} from '../model/static-pages.model';
import {StaticPagesService} from '../services/static-pages.service';
import {
  StaticPagesActions,
  contactAction,
  contactEffectError,
  contactEffectFailure,
  contactEffectSuccess,
  cookiePolicyAction,
  cookiePolicyEffectError,
  cookiePolicyEffectFailure,
  cookiePolicyEffectSuccess,
  howtoOrderPageAction,
  howtoOrderPageEffectError,
  howtoOrderPageEffectFailure,
  howtoOrderPageEffectSuccess,
  legalNoticeAction,
  legalNoticeEffectError,
  legalNoticeEffectFailure,
  legalNoticeEffectSuccess,
  privacyPolicyAction,
  privacyPolicyEffectError,
  privacyPolicyEffectFailure,
  privacyPolicyEffectSuccess,
  termsAndConditionsAction,
  termsAndConditionsEffectError,
  termsAndConditionsEffectFailure,
  termsAndConditionsEffectSuccess,
  welcomePageAction,
  welcomePageEffectSuccess,
} from './static-pages.actions';

@Injectable()
export class StaticPagesEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly staticPagesService = inject(StaticPagesService);
  readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);

  globalToast = 'global-toast';
  unableToPerform = 'Common.UnableToPerformThisActionPleaseContactSupport';
  termsAndConditions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaticPagesActions.TermsAndConditions),
      switchMap((action: ReturnType<typeof termsAndConditionsAction>) => this.termsAndConditions(action))
    )
  );

  legalNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaticPagesActions.LegalNotice),
      switchMap((action: ReturnType<typeof legalNoticeAction>) => this.legalNotice(action))
    )
  );

  privacyPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaticPagesActions.PrivacyPolicy),
      switchMap((action: ReturnType<typeof privacyPolicyAction>) => this.privacyPolicy(action))
    )
  );

  cookiePolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaticPagesActions.CookiePolicy),
      switchMap((action: ReturnType<typeof cookiePolicyAction>) => this.cookiePolicy(action))
    )
  );

  contact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaticPagesActions.Contact),
      switchMap((action: ReturnType<typeof contactAction>) => this.contact(action))
    )
  );

  welcomePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaticPagesActions.WelcomePage),
      switchMap((action: ReturnType<typeof welcomePageAction>) => this.welcomePage(action))
    )
  );

  howtoOrderPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaticPagesActions.HowtoOrderPage),
      switchMap((action: ReturnType<typeof howtoOrderPageAction>) => this.howtoOrderPage(action))
    )
  );

  private termsAndConditions(action: ReturnType<typeof termsAndConditionsAction>) {
    return this.staticPagesService.getStaticPage(action.request.key, action.request.languageId).pipe(
      map((response: IStaticPages) => {
        return termsAndConditionsEffectSuccess({response});
      }),
      catchError((errorResult: HttpErrorResponse) => this.handleCatchError(errorResult))
    );
  }

  private handleCatchError(errorResult: HttpErrorResponse) {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: this.translate.instant(this.unableToPerform),
      closable: true,
      life: EToastMessageOptions.LifeTime,
    });
    if (Array.isArray(errorResult.error)) {
      return this.handleArrayErrors(errorResult);
    } else if (typeof errorResult.error === 'string') {
      return this.handleStringErrors(errorResult);
    } else {
      return this.handleErrors(errorResult);
    }
  }

  private handleArrayErrors(errorResult: HttpErrorResponse) {
    return of(termsAndConditionsEffectFailure({reason: errorResult.error.join(', ')}));
  }

  private handleStringErrors(errorResult: HttpErrorResponse) {
    return of(termsAndConditionsEffectFailure({reason: errorResult.error}));
  }

  private handleErrors(errorResult: HttpErrorResponse) {
    return of(termsAndConditionsEffectError(errorResult));
  }

  private legalNotice(action: ReturnType<typeof legalNoticeAction>) {
    return this.staticPagesService.getStaticPage(action.request.key, action.request.languageId).pipe(
      map((response: IStaticPages) => {
        return legalNoticeEffectSuccess({response});
      }),
      catchError((errorResult: HttpErrorResponse) => this.handleLegalNoticeCatchError(errorResult))
    );
  }

  private handleLegalNoticeCatchError(errorResult: HttpErrorResponse) {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: this.translate.instant(this.unableToPerform),
      closable: true,
      life: EToastMessageOptions.LifeTime,
    });
    if (Array.isArray(errorResult.error)) {
      return this.handleLegalNoticeArrayErrors(errorResult);
    } else if (typeof errorResult.error === 'string') {
      return this.handleLegalNoticeStringErrors(errorResult);
    } else {
      return this.handleLegalNoticeErrors(errorResult);
    }
  }

  private handleLegalNoticeArrayErrors(errorResult: HttpErrorResponse) {
    return of(legalNoticeEffectFailure({reason: errorResult.error.join(', ')}));
  }

  private handleLegalNoticeStringErrors(errorResult: HttpErrorResponse) {
    return of(legalNoticeEffectFailure({reason: errorResult.error}));
  }

  private handleLegalNoticeErrors(errorResult: HttpErrorResponse) {
    return of(legalNoticeEffectError(errorResult));
  }

  private privacyPolicy(action: ReturnType<typeof privacyPolicyAction>) {
    return this.staticPagesService.getStaticPage(action.request.key, action.request.languageId).pipe(
      map((response: IStaticPages) => {
        return privacyPolicyEffectSuccess({response});
      }),
      catchError((errorResult: HttpErrorResponse) => this.handlePrivacyPolicyCatchError(errorResult))
    );
  }

  private handlePrivacyPolicyCatchError(errorResult: HttpErrorResponse) {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: this.translate.instant(this.unableToPerform),
      closable: true,
      life: EToastMessageOptions.LifeTime,
    });
    if (Array.isArray(errorResult.error)) {
      return this.handlePrivacyPolicyArrayErrors(errorResult);
    } else if (typeof errorResult.error === 'string') {
      return this.handlePrivacyPolicyStringErrors(errorResult);
    } else {
      return this.handlePrivacyPolicyErrors(errorResult);
    }
  }

  private handlePrivacyPolicyArrayErrors(errorResult: HttpErrorResponse) {
    return of(privacyPolicyEffectFailure({reason: errorResult.error.join(', ')}));
  }

  private handlePrivacyPolicyStringErrors(errorResult: HttpErrorResponse) {
    return of(privacyPolicyEffectFailure({reason: errorResult.error}));
  }

  private handlePrivacyPolicyErrors(errorResult: HttpErrorResponse) {
    return of(privacyPolicyEffectError(errorResult));
  }

  private cookiePolicy(action: ReturnType<typeof cookiePolicyAction>) {
    return this.staticPagesService.getStaticPage(action.request.key, action.request.languageId).pipe(
      map((response: IStaticPages) => {
        return cookiePolicyEffectSuccess({response});
      }),
      catchError((errorResult: HttpErrorResponse) => this.handleCookiePolicyCatchError(errorResult))
    );
  }

  private handleCookiePolicyCatchError(errorResult: HttpErrorResponse) {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: this.translate.instant(this.unableToPerform),
      closable: true,
      life: EToastMessageOptions.LifeTime,
    });
    if (Array.isArray(errorResult.error)) {
      return this.handleCookiePolicyArrayErrors(errorResult);
    } else if (typeof errorResult.error === 'string') {
      return this.handleCookiePolicyStringErrors(errorResult);
    } else {
      return this.handleCookiePolicyErrors(errorResult);
    }
  }

  private handleCookiePolicyArrayErrors(errorResult: HttpErrorResponse) {
    return of(cookiePolicyEffectFailure({reason: errorResult.error.join(', ')}));
  }

  private handleCookiePolicyStringErrors(errorResult: HttpErrorResponse) {
    return of(cookiePolicyEffectFailure({reason: errorResult.error}));
  }

  private handleCookiePolicyErrors(errorResult: HttpErrorResponse) {
    return of(cookiePolicyEffectError(errorResult));
  }

  private contact(action: ReturnType<typeof contactAction>) {
    return this.staticPagesService.getStaticPage(action.request.key, action.request.languageId).pipe(
      map((response: IStaticPages) => {
        return contactEffectSuccess({response});
      }),
      catchError((errorResult: HttpErrorResponse) => this.handleContactCatchError(errorResult))
    );
  }

  private welcomePage(action: ReturnType<typeof welcomePageAction>) {
    return this.staticPagesService.getStaticPage(action.request.key, action.request.languageId).pipe(
      map((response: IStaticPages) => {
        return welcomePageEffectSuccess({response});
      }),
      catchError((errorResult: HttpErrorResponse) => this.handleContactCatchError(errorResult))
    );
  }

  private howtoOrderPage(action: ReturnType<typeof howtoOrderPageAction>) {
    return this.staticPagesService.getStaticPage(action.request.key, action.request.languageId).pipe(
      map((response: IStaticPages) => {
        return howtoOrderPageEffectSuccess({response});
      }),
      catchError((errorResult: HttpErrorResponse) => this.handleHowtoOrderCatchError(errorResult))
    );
  }

  private handleContactCatchError(errorResult: HttpErrorResponse) {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: this.translate.instant(this.unableToPerform),
      closable: true,
      life: EToastMessageOptions.LifeTime,
    });
    if (Array.isArray(errorResult.error)) {
      return this.handleContactArrayErrors(errorResult);
    } else if (typeof errorResult.error === 'string') {
      return this.handleContactStringErrors(errorResult);
    } else {
      return this.handleContactErrors(errorResult);
    }
  }

  private handleContactArrayErrors(errorResult: HttpErrorResponse) {
    return of(contactEffectFailure({reason: errorResult.error.join(', ')}));
  }

  private handleContactStringErrors(errorResult: HttpErrorResponse) {
    return of(contactEffectFailure({reason: errorResult.error}));
  }

  private handleContactErrors(errorResult: HttpErrorResponse) {
    return of(contactEffectError(errorResult));
  }

  private handleHowtoOrderCatchError(errorResult: HttpErrorResponse) {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: this.translate.instant(this.unableToPerform),
      closable: true,
      life: EToastMessageOptions.LifeTime,
    });
    if (Array.isArray(errorResult.error)) {
      return this.handleHowtoOrderArrayErrors(errorResult);
    } else if (typeof errorResult.error === 'string') {
      return this.handleHowtoOrderStringErrors(errorResult);
    } else {
      return this.handleHowtoOrderErrors(errorResult);
    }
  }

  private handleHowtoOrderArrayErrors(errorResult: HttpErrorResponse) {
    return of(howtoOrderPageEffectFailure({reason: errorResult.error.join(', ')}));
  }

  private handleHowtoOrderStringErrors(errorResult: HttpErrorResponse) {
    return of(howtoOrderPageEffectFailure({reason: errorResult.error}));
  }

  private handleHowtoOrderErrors(errorResult: HttpErrorResponse) {
    return of(howtoOrderPageEffectError(errorResult));
  }
}
